/**
 * localStorage utility functions with type safety and error handling
 */

const STORAGE_PREFIX = 'meeting-cost-counter';

/**
 * Get localStorage instance (works in browser and test environments)
 */
function getLocalStorage(): Storage | null {
  if (typeof window !== 'undefined' && window.localStorage) {
    return window.localStorage;
  }
  if (typeof global !== 'undefined' && (global as any).localStorage) {
    return (global as any).localStorage;
  }
  return null;
}

/**
 * Get a value from localStorage with type safety
 */
export function getFromStorage<T>(key: string, defaultValue: T): T {
  const storage = getLocalStorage();
  if (!storage) {
    return defaultValue;
  }

  try {
    const item = storage.getItem(`${STORAGE_PREFIX}.${key}`);
    if (item === null) {
      return defaultValue;
    }
    return JSON.parse(item) as T;
  } catch (error) {
    console.warn(`Failed to read from localStorage for key "${key}":`, error);
    return defaultValue;
  }
}

/**
 * Save a value to localStorage with error handling
 */
export function saveToStorage<T>(key: string, value: T): boolean {
  const storage = getLocalStorage();
  if (!storage) {
    return false;
  }

  try {
    storage.setItem(`${STORAGE_PREFIX}.${key}`, JSON.stringify(value));
    return true;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      console.error('localStorage quota exceeded. Clearing old data...');
      // Optionally clear old data or notify user
    } else {
      console.warn(`Failed to save to localStorage for key "${key}":`, error);
    }
    return false;
  }
}

/**
 * Remove a value from localStorage
 */
export function removeFromStorage(key: string): void {
  const storage = getLocalStorage();
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(`${STORAGE_PREFIX}.${key}`);
  } catch (error) {
    console.warn(`Failed to remove from localStorage for key "${key}":`, error);
  }
}

/**
 * Clear all app-related localStorage data
 */
export function clearAllStorage(): void {
  const storage = getLocalStorage();
  if (!storage) {
    return;
  }

  try {
    // Use length and key() to iterate through all keys
    // This works with both real localStorage and our mock
    const keysToRemove: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && key.startsWith(`${STORAGE_PREFIX}.`)) {
        keysToRemove.push(key);
      }
    }
    // Remove keys after iteration to avoid index issues
    keysToRemove.forEach((key) => {
      storage.removeItem(key);
    });
  } catch (error) {
    console.warn('Failed to clear localStorage:', error);
  }
}
